/**
 * File picker filter for company profile artifacts (FEC + DSN FR ingestors).
 */
export const COMPANY_PROFILE_ARTIFACT_FILE_ACCEPT =
  '.fec,.dsn,.txt,.csv,.tsv,text/plain,text/csv,text/tab-separated-values,application/csv';

/** Cabinet inbox upload: fallback type picker when server cannot auto-detect (see CompanyProfileIngestorFileDetectorInterface). */
export const CABINET_INBOX_ARTIFACT_TYPES = ['fec_fr', 'dsn_fr'] as const;
export type CabinetInboxArtifactType = (typeof CABINET_INBOX_ARTIFACT_TYPES)[number];

export type ProfileArtifactTypeFromName = 'fec_fr' | 'dsn_fr' | 'ambiguous';

export type ProfileArtifactType = 'fec_fr' | 'dsn_fr';

export type ProfileArtifactDetection =
  | { kind: 'certain'; artifactType: ProfileArtifactType }
  | { kind: 'ambiguous'; artifactTypes: ProfileArtifactType[] }
  | { kind: 'unknown' };

/** Matches {@see ArtifactTypeDetectorService::probeByteLimit()} and ingestor sniff probes. */
export const PROFILE_ARTIFACT_SNIFF_PROBE_BYTES = 65536;

const FEC_HEADER_DEBIT_CREDIT = [
  'JournalCode',
  'JournalLib',
  'EcritureNum',
  'EcritureDate',
  'CompteNum',
  'CompteLib',
  'CompAuxNum',
  'CompAuxLib',
  'PieceRef',
  'PieceDate',
  'EcritureLib',
  'Debit',
  'Credit',
  'EcritureLet',
  'DateLet',
  'ValidDate',
  'Montantdevise',
  'Idevise',
] as const;

const FEC_HEADER_MONTANT_SENS = [
  'JournalCode',
  'JournalLib',
  'EcritureNum',
  'EcritureDate',
  'CompteNum',
  'CompteLib',
  'CompAuxNum',
  'CompAuxLib',
  'PieceRef',
  'PieceDate',
  'EcritureLib',
  'Montant',
  'Sens',
  'EcritureLet',
  'DateLet',
  'ValidDate',
  'Montantdevise',
  'Idevise',
] as const;

const FEC_COLUMN_COUNT = 18;

/**
 * Maps extension to ingestor; `.txt` is ambiguous (FEC and DSN both allow it).
 */
export function guessProfileArtifactTypeFromFilename(filename: string): ProfileArtifactTypeFromName {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'dsn') {
    return 'dsn_fr';
  }
  if (ext === 'fec' || ext === 'csv' || ext === 'tsv') {
    return 'fec_fr';
  }
  if (ext === 'txt') {
    return 'ambiguous';
  }
  return 'ambiguous';
}

/** Parity with {@see DsnFrParser::lineLooksLikeRubrique()}. */
export function lineLooksLikeDsnRubrique(line: string): boolean {
  return /^S[0-9]{2}\.G[0-9]{2}\.[0-9]{2}\.[0-9]{3}/.test(line);
}

/** Parity with {@see FecFrIngestor::fecStandardFilenameBasename()}. */
export function fecStandardFilenameBasename(filename: string): boolean {
  const base = filename.split(/[/\\]/).pop()?.split('.').slice(0, -1).join('.') ?? '';
  if (base === '') {
    const noExt = filename.split(/[/\\]/).pop() ?? '';
    return /^(\d{9})fec\d{8}$/i.test(noExt);
  }
  return /^(\d{9})fec\d{8}$/i.test(base);
}

/** Parity with {@see FecFrParser::lineIsRecognizedFecHeader()}. */
export function lineIsRecognizedFecHeader(line: string): boolean {
  let trimmed = line.replace(/\r?\n$/, '');
  if (trimmed.startsWith('\uFEFF')) {
    trimmed = trimmed.slice(1);
  }
  if (trimmed === '') {
    return false;
  }

  const delimiter = trimmed.includes('|') ? '|' : trimmed.includes('\t') ? '\t' : null;
  if (delimiter === null) {
    return false;
  }

  const header = trimmed.split(delimiter);
  if (header.length !== FEC_COLUMN_COUNT) {
    return false;
  }

  const debitCredit = header.every((col, i) => col === FEC_HEADER_DEBIT_CREDIT[i]);
  if (debitCredit) {
    return true;
  }

  return header.every((col, i) => col === FEC_HEADER_MONTANT_SENS[i]);
}

function stripLeadingBom(line: string): string {
  return line.startsWith('\uFEFF') ? line.slice(1) : line;
}

function firstNonEmptyLineFromText(text: string): string | null {
  for (const raw of text.split(/\r\n|\n|\r/)) {
    const line = stripLeadingBom(raw.replace(/\r?\n$/, ''));
    if (line !== '') {
      return line;
    }
  }
  return null;
}

function collectCertainArtifactTypes(filename: string, firstLine: string | null): ProfileArtifactType[] {
  const certains: ProfileArtifactType[] = [];
  const fromName = guessProfileArtifactTypeFromFilename(filename);
  if (fromName !== 'ambiguous') {
    certains.push(fromName);
  }
  if (fecStandardFilenameBasename(filename)) {
    certains.push('fec_fr');
  }
  if (firstLine !== null) {
    if (lineLooksLikeDsnRubrique(firstLine)) {
      certains.push('dsn_fr');
    }
    if (lineIsRecognizedFecHeader(firstLine)) {
      certains.push('fec_fr');
    }
  }
  return [...new Set(certains)];
}

function outcomeFromCertains(certains: ProfileArtifactType[]): ProfileArtifactDetection {
  if (certains.length === 1) {
    return { kind: 'certain', artifactType: certains[0] };
  }
  if (certains.length > 1) {
    return { kind: 'ambiguous', artifactTypes: certains };
  }
  return { kind: 'unknown' };
}

/**
 * Bounded client sniff before per-dossier upload (PHP ingestors: {@see DsnFrIngestor}, {@see FecFrIngestor}).
 */
export async function detectProfileArtifactType(file: File): Promise<ProfileArtifactDetection> {
  const slice = file.slice(0, PROFILE_ARTIFACT_SNIFF_PROBE_BYTES);
  const text = await slice.text();
  const firstLine = firstNonEmptyLineFromText(text);
  return outcomeFromCertains(collectCertainArtifactTypes(file.name, firstLine));
}

/** Sync helper for tests and pre-read paths when file body is already available. */
export function detectProfileArtifactTypeFromProbe(
  filename: string,
  probeText: string,
): ProfileArtifactDetection {
  return outcomeFromCertains(collectCertainArtifactTypes(filename, firstNonEmptyLineFromText(probeText)));
}
